import { isMacPlatform } from '../utils/platform'

/**
 * Display-only map of drawing-tool → keyboard-shortcut label, keyed by overlay
 * name. The single source the DrawingBar flyout and the chart-body right-click
 * menu both read, so the two surfaces never drift on shortcut labels.
 *
 * Every drawing shortcut is an Option/Alt chord (bare letters belong to the
 * ticker-entry layer). Labels are platform-aware: ⌥H on macOS, Alt+H
 * elsewhere.
 *
 * This is the DISPLAY copy. The actual keydown dispatch lives in
 * `ChartManager.hotkeyHandler` (keyed by physical e.code, not tool name); keep
 * the two in sync when a binding changes.
 */
const TOOL_CHORDS: Record<string, { key: string; shift?: boolean }> = {
  horizontalStraightLine: { key: 'H' },
  verticalStraightLine: { key: 'V' },
  rayLine: { key: 'R' },
  // Labeled as the shifted sibling of the H line chord; Option+Shift+R also
  // works (silent alias, see ChartManager.hotkeyHandler).
  horizontalRayLine: { key: 'H', shift: true },
  fibonacciLine: { key: 'F' },
  positionPlan: { key: 'P' },
}

function formatChord(chord: { key: string; shift?: boolean }, mac: boolean): string {
  return mac
    ? `⌥${chord.shift ? '⇧' : ''}${chord.key}`
    : `Alt+${chord.shift ? 'Shift+' : ''}${chord.key}`
}

/** Build the label map for a given platform (exported for tests). */
export function toolHotkeysFor(mac: boolean): Record<string, string> {
  return Object.fromEntries(
    Object.entries(TOOL_CHORDS).map(([name, chord]) => [name, formatChord(chord, mac)]),
  )
}

export const TOOL_HOTKEYS: Record<string, string> = toolHotkeysFor(isMacPlatform())
